import { useState, useMemo } from "react";
import { Card } from "react-bootstrap";
import { useIntl, FormattedMessage } from "react-intl";

import DataTable from "components/organisms/DataTable/DataTable";
import { getProviderTeams, getProviderTeamsOverview } from "api";
import useTableRequest from "hooks/useTableRequest";
import GlobalTableSearch from "components/organisms/DataTable/components/GlobalTableSearch";
import Button from "components/atoms/Button/Button";
import ManageTeamsDialog from "./ManageTeamsDialog";

function Teams() {
  const intl = useIntl();

  const [showTeamsDialog, setShowTeamsDialog] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null);

  const columns = useMemo(
    () => [
      {
        Header: (
          <span className="pl-9 ">
            <FormattedMessage id="settings.provider.team.teamName" defaultMessage="Team name" />
          </span>
        ),
        accessor: "team",
        Cell: ({ value }: any) => <p className="pl-9 pt-8 pb-8 mb-0">{value}</p>,
      },
      {
        Header: (
          <span className="pl-9 ">
            <FormattedMessage id="settings.provider.team.shippers" defaultMessage="Shippers" />
          </span>
        ),
        accessor: "shippers",
        Cell: ({ value }: any) => <p className="pl-9 pt-8 pb-8 mb-0 overflow-hidden text-truncate w-150-px">{value}</p>,
      },
      {
        Header: (
          <span className="pl-9 ">
            <FormattedMessage id="general.freightModes" defaultMessage="Freight modes" />
          </span>
        ),
        accessor: "serviceable_freight_modes",
        Cell: ({ value }: any) => <p className="pl-9 pt-8 pb-8 mb-0 overflow-hidden text-truncate w-150-px">{value}</p>,
      },
      {
        Header: (
          <span className="pl-9 ">
            <FormattedMessage id="settings.provider.team.serviceAreas" defaultMessage="Service areas" />
          </span>
        ),
        accessor: "service_areas",
        Cell: ({ value }: any) => <p className="pl-9 pt-8 pb-8 mb-0 overflow-hidden text-truncate w-150-px">{value}</p>,
      },
      {
        Header: (
          <span className="pl-9 ">
            <FormattedMessage id="general.address" defaultMessage="Address" />
          </span>
        ),
        accessor: "address",
        Cell: ({ value }: any) => <p className="pl-9 pt-8 pb-8 mb-0">{value}</p>,
      },
      {
        Header: (
          <span className="pl-9 ">
            <FormattedMessage id="general.status" defaultMessage="Status" />
          </span>
        ),
        accessor: "is_active",
        Cell: ({ value }: any) => <p className="pl-9 pt-8 pb-8 mb-0">{value && "True"}</p>,
      },
    ],
    [],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const { instance, fetching, totalData, refetch } = useTableRequest(getProviderTeams, columns, {
    params: {},
    filters: (globalFilter) => ({
      ...(globalFilter.query?.length && { query: globalFilter.query }),
      ...(globalFilter.payment_location?.length && { payment_location: globalFilter.payment_location }),
    }),
    deps: [location],
  });

  const exportTeams = () => {
    const config = { responseType: "blob" };
    getProviderTeamsOverview(config).then((res) => {
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    });
  };

  return (
    <Card body className="shadow-none quote-container">
      <ManageTeamsDialog
        setSelectedTeam={setSelectedTeam}
        selectedTeam={selectedTeam}
        show={showTeamsDialog}
        close={() => setShowTeamsDialog(false)}
        refetch={refetch}
      />
      <DataTable
        tableInstance={instance}
        totalData={totalData}
        isLoading={fetching}
        draggable={false}
        onRowClick={(row: any) => {
          setSelectedTeam(row.original.team_id);
          setShowTeamsDialog(true);
        }}
        headerLeft={
          <GlobalTableSearch
            currentFilters={instance.state.globalFilter}
            setFilters={instance.setGlobalFilter}
            filterKey="query"
            eventContext="Team members"
          />
        }
        headerRight={
          <Button
            label={intl.formatMessage({
              id: "settings.teams.exportOverview",
              defaultMessage: "Export overview",
            })}
            className="rounded-pill ml-3"
            style={{ height: "26px" }}
            onClick={exportTeams}
          />
        }
      />
    </Card>
  );
}

export default Teams;
